import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { DatePickerField } from 'components/form-control';
import yup from 'config/yup.custom';
import { convertTimeToUTC, mergeDateTime, NotifyService } from 'helpers';
import { useDateTime } from 'hooks';
import { PURCHASE_HISTORY_ITEM_RESPONSE } from 'models/subscription';
import { memo, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { updatePurchaseHistoryApi } from 'services/subscription';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setLoadingPopup } from 'store/reducers/global';

interface PopupEditPurchaseHistoryProps {
  open: boolean;
  subject?: PURCHASE_HISTORY_ITEM_RESPONSE;
  onSuccess: () => void;
  handleClosePopup: () => void;
}

interface IFormInputs {
  expiredAt: string;
}

const PopupEditPurchaseHistory = memo(
  ({
    open,
    subject,
    onSuccess,
    handleClosePopup,
  }: PopupEditPurchaseHistoryProps) => {
    const { moment } = useDateTime();
    const { loadingPopup } = useAppSelector((state) => state.globalReducer);
    const dispatch = useAppDispatch();

    const schema = useMemo(() => {
      return yup.object({
        expiredAt: yup.string().required('Expired date is required'),
      });
    }, []);

    const form = useForm<IFormInputs>({
      resolver: yupResolver(schema),
      mode: 'onChange',
      defaultValues: {
        expiredAt: '',
      },
    });

    const { reset, handleSubmit, setValue, watch } = form;

    const onClosePopup = () => {
      reset({
        expiredAt: '',
      });
      handleClosePopup();
    };

    useEffect(() => {
      if (subject && open) {
        setValue('expiredAt', subject.expiredAt);
      }
    }, [subject, open, setValue, moment]);

    const onSubmit = async (data: IFormInputs) => {
      if (!subject) return;

      try {
        dispatch(setLoadingPopup(true));
        const formattedDate = convertTimeToUTC(
          mergeDateTime(data.expiredAt, subject.expiredAt)
        );

        await updatePurchaseHistoryApi(subject.id, {
          expiredAt: formattedDate,
        });

        onSuccess();
        onClosePopup();

        NotifyService.success('Purchase history updated successfully');
      } catch (error: any) {
        NotifyService.error(
          error?.response?.data?.message || 'Failed to update purchase history'
        );
      } finally {
        dispatch(setLoadingPopup(false));
      }
    };

    return (
      <Dialog
        open={open}
        sx={{
          '& .MuiPaper-root': {
            width: 500,
            borderRadius: 0,
          },
        }}
      >
        <Grid>
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 25px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '100%',
              }}
            >
              Edit Purchase History
            </Typography>
            <IconButton onClick={onClosePopup} sx={{ padding: 0 }}>
              <CloseCircle />
            </IconButton>
          </Grid>
          <Divider sx={{ borderColor: '#DEE2E6' }} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack height="100%" justifyContent="space-between">
              <Stack spacing="16px" p="25px">
                <Grid>
                  <DatePickerField
                    title="Expired Date"
                    form={form}
                    name="expiredAt"
                    minDate={
                      watch('expiredAt')
                        ? moment(watch('expiredAt'))
                        : undefined
                    }
                  />
                </Grid>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing="8px"
                p="0px 25px 25px"
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{
                    width: '233.5px',
                    height: '30px',
                  }}
                  onClick={onClosePopup}
                >
                  Cancel
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  sx={{
                    width: '233.5px',
                    height: '30px',
                  }}
                  type="submit"
                  disabled={loadingPopup}
                >
                  Update
                </Button>
              </Stack>
            </Stack>
          </form>
        </Grid>
      </Dialog>
    );
  }
);

export default PopupEditPurchaseHistory;
